import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CalendarDays } from "lucide-react";
import heroImage from "@/assets/blog/spring-break-hero.jpg";

const BlogSpringBreak2026 = () => {
  const related = getRelatedPosts("spring-break-2026-sons-river-ranch", 3);
  return (
    <div className="min-h-screen">
      <SEOHead
        title="Spring Break 2026 at Son's River Ranch — We're Open"
        description="Son's River Ranch is open for spring break 2026! Tubing, kayaking, glamping & camping on the Guadalupe. Use code SB2026 for 25% off."
        canonical="/blog/spring-break-2026-sons-river-ranch"
        ogImage="/og/blog-spring-break.jpg"
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
            { "@type": "ListItem", position: 2, name: "Blog", item: "https://sonsguadalupe.com/blog" },
            { "@type": "ListItem", position: 3, name: "Spring Break 2026 at Son's River Ranch", item: "https://sonsguadalupe.com/blog/spring-break-2026-sons-river-ranch" },
          ],
        }}
      />
      <Header />
      <main className="pt-24 pb-16">
        {/* Hero */}
        <div className="relative h-[50vh] min-h-[360px]">
          <img
            src={heroImage}
            alt="Spring Break at Son's River Ranch on the Guadalupe River"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="container-narrow mx-auto">
              <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
                <CalendarDays className="w-4 h-4" />
                March 14, 2026
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white max-w-3xl leading-tight">
                Spring Break 2026 at Son's River Ranch — We're Open & the River Is Calling!
              </h1>
            </div>
          </div>
        </div>

        {/* Content */}
        <article className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>

            <div className="prose prose-lg max-w-none text-foreground space-y-6">
              <p className="text-xl leading-relaxed">
                Spring break is here, and <strong>Son's River Ranch is officially open</strong> for the season! If you've been dreaming about a day on the river — or a full weekend under the stars — now is the time to book.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">
                The Perfect Time to Hit the River
              </h2>
              <p>
                We're incredibly fortunate this year — the weather forecast for spring break is looking <strong>exceptional</strong>. Warm, sunny days are on tap, and the Guadalupe River is flowing beautifully. Best of all, the water is unseasonably nice. It's not too cold, making it perfect for tubing, kayaking, and splashing around with the kids.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">
                So Much to Do at Son's River Ranch
              </h2>
              <p>
                Whether you're looking for a laid-back float or a full adventure weekend, Son's River Ranch has something for everyone:
              </p>
              <ul className="space-y-3 list-none pl-0">
                <li className="flex items-start gap-3">
                  <span className="text-coral-orange font-bold text-lg">🛶</span>
                  <span><strong>Kayaking</strong> — Paddle through scenic Hill Country canyon views on crystal-clear water.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-coral-orange font-bold text-lg">🟡</span>
                  <span><strong>Tubing</strong> — Grab a tube and float the Guadalupe. Your float ends right back at your home base — not a parking lot.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-coral-orange font-bold text-lg">⛺</span>
                  <span><strong>Glamping & Camping</strong> — Stay overnight in our riverside bell tents with real beds and AC, or bring your own gear and camp under the stars.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-coral-orange font-bold text-lg">🔥</span>
                  <span><strong>Fire Pits & BBQ</strong> — End the day around a fire pit making s'mores and swapping stories.</span>
                </li>
              </ul>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">
                Create Memories That Last a Lifetime
              </h2>
              <p>
                Spring break isn't just about getting away — it's about being <em>together</em>. Whether it's watching your kids float through their first set of rapids, cooking burgers on the grill while the sun sets over the river, or stargazing from your glamping tent — these are the moments your family will talk about for years.
              </p>
              <p>
                Son's River Ranch is the kind of place where you can unplug, slow down, and just enjoy each other. No crowds, no chaos — just your family, the river, and the beautiful Texas Hill Country.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">
                Spring Break Special — 25% OFF All Bookings
              </h2>
              <p>
                To celebrate the start of the season, we're offering <strong>25% off all bookings</strong> with promo code:
              </p>
              <div className="bg-card rounded-xl border border-border p-6 text-center my-8">
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-2">
                  Promo Code
                </p>
                <p className="font-heading text-4xl font-bold text-coral-orange tracking-widest">
                  SB2026
                </p>
                <p className="text-muted-foreground mt-2 text-sm">
                  25% off all bookings — limited time only
                </p>
              </div>

              <p>
                Don't wait — spring break weekends fill up fast, and spots are limited. Lock in your dates now and get ready for the best river getaway of the year.
              </p>

              <div className="text-center mt-10">
                <a href="https://sonsriverranch.com/springbreak" target="_blank" rel="noopener noreferrer">
                  <Button variant="default" size="lg" className="text-lg px-10 py-6">
                    Book Now — Spring Break 2026
                  </Button>
                </a>
              </div>

              <p className="text-center text-muted-foreground text-sm mt-4">
                See you on the river! 🌊
              </p>
            </div>
          </div>
        </article>
        <RelatedReads posts={related} />

      </main>
      <SEOContentBlock
        eyebrow="Plan Your Visit"
        heading="Spring Break & Family Getaways Near New Braunfels"
        paragraphs={[
          "Spring break is one of the best times for family getaways near New Braunfels — warmer days, lighter crowds, and a Guadalupe River that's flowing perfectly for tubing the Guadalupe River. Pair your stay with cabana rentals on the Guadalupe River, glamping near New Braunfels, or tent camping on River Road for the full Hill Country experience.",
          "From first-time floaters to multi-generational trips, Son's Guadalupe is built for fun things to do with kids near New Braunfels — without the chaos of crowded outfitters.",
        ]}
        links={[
          { label: "Cabanas", to: "/cabanas" },
          { label: "Tubing", to: "/tubing" },
          { label: "Glamping & Camping", to: "/glamping" },
          { label: "Book Now", to: "/book-now" },
        ]}
      />
      <Footer />
    </div>
  );
};

export default BlogSpringBreak2026;
